import { PermissionGate } from "@/components/common/PermissionGate"
import { Button } from "@/components/ui/button"
import { PERMISSIONS } from "@/constants/permissions"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { X } from "lucide-react"
import { useEffect, useMemo } from "react"
import { useNavigate } from "react-router"
import { useCustomerList } from "../../api/customersApi"
import { useProductList } from "../../api/productsApi"
import { conformityCreatePath } from "../customer-requests/routes"
import { FooterActionBar } from "./FooterActionBar"
import { SectionLabel } from "./SectionLabel"

interface RecipientRow {
  id: string
  name: string
}

interface GenerateConformityPanelProps {
  entityType: "product" | "customer"
  selectedIds: Set<string>
  /** Removing a row unchecks the same id back in the sibling list panel —
   * this is the one shared source of truth. */
  onRemoveIds: (ids: string[]) => void
}

/**
 * Replaces the normal detail view when one or more rows are checkbox-selected
 * in the sibling Product/Customer list panel — mirrors SendInquiryPanel's
 * shape (resolve selected ids into full rows, list them removably, hand off
 * to the next screen) but simpler: no supplier-grouping step, just a straight
 * list, then on to the conformity generate wizard with all ids pre-checked.
 */
export function GenerateConformityPanel({
  entityType,
  selectedIds,
  onRemoveIds,
}: GenerateConformityPanelProps) {
  const navigate = useNavigate()
  const { translate } = useTranslation()

  // Generous per-page limit — this is a dedicated one-off fetch to resolve the
  // checkbox-selected ids into full rows, not the interactive scrollable list.
  // Both hooks are called unconditionally (rules of hooks); only the one
  // matching `entityType` is used below.
  const {
    data: productData,
    fetchNextPage: fetchNextProductPage,
    hasNextPage: hasNextProductPage,
    isFetchingNextPage: isFetchingNextProductPage,
    isLoading: isLoadingProducts,
  } = useProductList({ limit: 200 })
  const {
    data: customerData,
    fetchNextPage: fetchNextCustomerPage,
    hasNextPage: hasNextCustomerPage,
    isFetchingNextPage: isFetchingNextCustomerPage,
    isLoading: isLoadingCustomers,
  } = useCustomerList({ limit: 200 })

  const isLoading =
    entityType === "product" ? isLoadingProducts : isLoadingCustomers

  const rows = useMemo<RecipientRow[]>(() => {
    const source =
      entityType === "product"
        ? (productData?.pages.flatMap((p) => p.items) ?? [])
        : (customerData?.pages.flatMap((p) => p.items) ?? [])
    return source
      .filter((row) => selectedIds.has(row.id))
      .map((row) => ({ id: row.id, name: row.name }))
  }, [entityType, productData, customerData, selectedIds])

  // Every selected id already has a matching row loaded — no need to keep
  // paging just because more pages exist.
  const allSelectedResolved = rows.length >= selectedIds.size

  // Page through only as far as needed to resolve every selected id — a
  // selection can include rows beyond the first page, and those must not
  // silently drop out of the list, but once they're all found there's no
  // reason to keep fetching the rest of a large org's product/customer list
  // in the background. `isFetching*Page` is in the deps (not just
  // `hasNextPage`) so the effect re-fires each time a page settles —
  // `hasNextPage` alone can stay `true` across an entire multi-page load and
  // never re-trigger this effect on its own.
  useEffect(() => {
    if (allSelectedResolved) return
    if (
      entityType === "product" &&
      hasNextProductPage &&
      !isFetchingNextProductPage
    ) {
      fetchNextProductPage()
    }
    if (
      entityType === "customer" &&
      hasNextCustomerPage &&
      !isFetchingNextCustomerPage
    ) {
      fetchNextCustomerPage()
    }
  }, [
    entityType,
    allSelectedResolved,
    hasNextProductPage,
    hasNextCustomerPage,
    isFetchingNextProductPage,
    isFetchingNextCustomerPage,
    fetchNextProductPage,
    fetchNextCustomerPage,
  ])

  // Navigate off `selectedIds` directly, not `rows` — `rows` is only a
  // display projection of whatever pages have resolved so far, and clicking
  // Generate before paging finishes would otherwise silently drop any
  // selected id whose row hasn't loaded yet.
  function handleSubmit() {
    navigate(conformityCreatePath({ scope: entityType, ids: [...selectedIds] }))
  }

  return (
    <div className="absolute inset-0 flex flex-col">
      <div className="flex-1 overflow-y-auto p-4">
        <div className="flex flex-col gap-2">
          <SectionLabel>
            {translate(
              entityType === "product"
                ? "MC_MD_TAB_PRODUCTS"
                : "MC_MD_TAB_CUSTOMERS",
            )}
          </SectionLabel>
          <div className="flex flex-col gap-2">
            {rows.length === 0 && (
              <p className="text-xs text-grey5">
                {isLoading
                  ? translate("MC_INQ_DRAFT_ADD_ARTICLE_LOADING")
                  : translate("MC_INQ_SEND_NO_RECIPIENTS")}
              </p>
            )}
            {rows.map((row) => (
              <div
                key={row.id}
                className="flex items-center justify-between gap-2 rounded-lg border border-grey4 bg-grey2 px-4 py-3"
              >
                <span className="truncate text-sm font-semibold text-white">
                  {row.name}
                </span>
                <button
                  type="button"
                  onClick={() => onRemoveIds([row.id])}
                  className="text-grey5 hover:text-white"
                  aria-label={translate("MC_INQ_SEND_REMOVE", {
                    name: row.name,
                  })}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <FooterActionBar
        left={translate("MC_CONFORMITY_BULK_COUNT", {
          count: selectedIds.size,
        })}
      >
        <PermissionGate
          permission={PERMISSIONS.MC_CUSTOMER_DECLARATIONS_GENERATE}
        >
          <Button
            type="button"
            disabled={selectedIds.size === 0}
            onClick={handleSubmit}
          >
            {translate("MC_CONFORMITY_BTN_GENERATE")}
          </Button>
        </PermissionGate>
      </FooterActionBar>
    </div>
  )
}
